/**
 * @file - App component.
 */
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { EnvService } from './core/services/env/env.service';
import { FunnelGuardSyncService } from './core/services/driver/funnel-guard-sync.service';
import { ChatbotConfigService } from '@rideshare/hcb-chatbot';
import { UserDataService } from './core/services/user-data/user-data.service';
import { RoutePaths } from './shared/constants/route-paths.constant';

declare const fbq: any;

/**
 * App component.
 */
@Component({
    standalone: false,
    selector: 'octo-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'driver-dashboardfe';
    currentURL = '';
    showSideNav = false;
    isOctopusApp: boolean = false;
    showHeader: boolean = true;
    isBonus: boolean = false;
    isOrderTracking: boolean = false;
    maintenanceModeActiveFlag: string;
    pathName = globalThis.location.pathname;
    isRouteLoading: boolean = false;

    private subscription: Subscription = new Subscription();

    /**
     * Constructor.
     *
     * @param {Router} router - Router service.
     * @param {FunnelGuardSyncService} funnelGuardSyncService - Funnel guard sync service.
     * @param {ChangeDetectorRef} cdr - Change detector ref.
     */
    constructor(
        private readonly router: Router,
        private readonly funnelGuardSyncService: FunnelGuardSyncService,
        private readonly cdr: ChangeDetectorRef,
        private readonly envService: EnvService,
        private readonly userDataService: UserDataService,
        private readonly chatbotConfig: ChatbotConfigService
    ) {
        this.maintenanceModeActiveFlag = this.envService.maintenanceModeActiveFlag;
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((val: any) => {
                this.currentURL = val.urlAfterRedirects;
                this.showSideNav =
                    this.currentURL.includes('dashboard') ||
                    this.currentURL.includes('my-account');
                this.isBonus =
                    this.currentURL.includes(RoutePaths.JOIN) ||
                    this.currentURL.includes(RoutePaths.PROFILE) ||
                    this.currentURL.includes(RoutePaths.INFO) ||
                    this.currentURL.includes(RoutePaths.PAYMENT) ||
                    this.currentURL.includes(RoutePaths.PROOF_OF_RIDES) ||
                    this.currentURL.includes(RoutePaths.UBER_CONNECT_REQUIRED);
                if (val instanceof NavigationEnd) {
                    if (fbq && fbq !== undefined) fbq('track', 'PageView');
                }

                this.isOctopusApp = this.currentURL.includes(RoutePaths.DRIVER_APP_REDIRECT);
                this.isOrderTracking = this.currentURL.startsWith(`/${RoutePaths.ORDER_TRACKING}`);
                this.showHeader = this.currentURL !== `/${RoutePaths.LEADERBOARD}`;
            });
    }

    /**
     * Load script.
     *
     * @param {string} name - Name of the script.
     * @returns {Promise<any>} - Promise object.
     */
    loadScript(name: string): Promise<unknown> {
        return new Promise((resolve, reject) => {
            let script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = name;
            document.getElementsByTagName('head')[0].appendChild(script);
            /**
             * Resolve the promise once the script is loaded.
             */
            script.onload = () => {
                resolve({ script: name, loaded: true, status: 'Loaded' });
            };
        });
    }

    /**
     * On init.
     */
    ngOnInit() {

        // Subscribe to funnel guard loading state
        this.subscription = this.funnelGuardSyncService
            .getFunnelGuardSync()
            .subscribe((status) => {
                this.isRouteLoading = status === false;
                this.cdr.detectChanges();
            });

        // Chatbot needs the signed-in driver's name/email to submit a support ticket.
        this.subscription.add(
            this.userDataService.getUserSummary().subscribe((data: any) => {
                const name = [data?.first_name, data?.last_name].filter(Boolean).join(' ');
                this.chatbotConfig.setUser(name && data?.email ? { name, email: data.email } : null);
            })
        );

        this.loadScript(
            `https://maps.googleapis.com/maps/api/js?key=${this.envService.googlePlacesApiKey}&libraries=places&loading=async`
        ).then(() => {});
    }

    /**
     * Check if the app is in maintenance mode.
     *
     * @returns {boolean} - True if in maintenance mode, false otherwise.
     */
    isOnMaintenanceMode(): boolean {
        switch (this.pathName) {
            case `/${RoutePaths.JOIN}`:
            case `/${RoutePaths.INFO}`:
            case `/${RoutePaths.PROFILE}`:
            case `/${RoutePaths.TWO_FACTOR_CODE}`:
            case `/${RoutePaths.PAYMENT}`:
            case `/${RoutePaths.PROOF_OF_RIDES}`:
            case `/${RoutePaths.COMPLETE}`:
            case `/${RoutePaths.UBER_AUTHORIZE}`:
            case `/${RoutePaths.UBER_CONNECT_REQUIRED}`:
            case `/${RoutePaths.INCOMPLETE_AND_CONTACT_SUPPORT}`:
                return false;
            default:
                return (
                    this.maintenanceModeActiveFlag === 'true' ||
                    this.maintenanceModeActiveFlag === 'True'
                );
        }
    }

    /**
     * On destroy.
     */
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

}
